import Axios from "axios";
import SourceUpdatesAttributeChangeRow from "../domain/SourceUpdatesAttributeChangeRow";
import SourceUpdatesAttributeBreakdownRow from "../domain/SourceUpdatesAttributeBreakdownRow";
import SourceUpdatesBreakdownRow from "../domain/SourceUpdatesBreakdownRow";
import SourceUpdatesDailyPoint from "../domain/SourceUpdatesDailyPoint";
import SourceUpdatesSummary from "../domain/SourceUpdatesSummary";

export interface SourceUpdatesQuery {
    dateFrom?: string;
    dateTo?: string;
    organizationId?: string;
    sourceId?: string;
    attributeNames?: string[];
    sourceIds?: string[];
    changeTypes?: string[];
    similarityMin?: number | null;
    similarityMax?: number | null;
    numericDifferenceMin?: number | null;
    numericDifferenceMax?: number | null;
    orderBy?: string;
    top?: number;
    skip?: number;
}

export interface SourceUpdatesAttributeChangeStatsEntry {
    key: string;
    label: string;
    count: number;
}

export interface SourceUpdatesAttributeChangeStats {
    sources: SourceUpdatesAttributeChangeStatsEntry[];
    changeTypes: SourceUpdatesAttributeChangeStatsEntry[];
}

export interface SourceUpdatesAttributeChangesResult {
    total: number;
    data: SourceUpdatesAttributeChangeRow[];
    stats: SourceUpdatesAttributeChangeStats;
}

class SourceUpdatesService {

    readonly basePath = "/source-updates";

    public getSummary(query: SourceUpdatesQuery): Promise<SourceUpdatesSummary> {
        return Axios.get(`${this.basePath}/summary?${this.toQueryParams(query)}`)
            .then(value => SourceUpdatesSummary.fromPOJO(value.data));
    }

    public getDaily(query: SourceUpdatesQuery): Promise<SourceUpdatesDailyPoint[]> {
        return Axios.get(`${this.basePath}/daily?${this.toQueryParams(query)}`)
            .then(value => value.data.map((item: any) => SourceUpdatesDailyPoint.fromPOJO(item)));
    }

    public getBreakdown(query: SourceUpdatesQuery): Promise<SourceUpdatesBreakdownRow[]> {
        return Axios.get(`${this.basePath}/breakdown?${this.toQueryParams(query)}`)
            .then(value => value.data.map((item: any) => SourceUpdatesBreakdownRow.fromPOJO(item)));
    }

    public getAttributeBreakdown(query: SourceUpdatesQuery): Promise<SourceUpdatesAttributeBreakdownRow[]> {
        return Axios.get(`${this.basePath}/attribute-breakdown?${this.toQueryParams(query)}`)
            .then(value => value.data.map((item: any) => SourceUpdatesAttributeBreakdownRow.fromPOJO(item)));
    }

    public getAttributeChanges(query: SourceUpdatesQuery): Promise<SourceUpdatesAttributeChangesResult> {
        return Axios.get(`${this.basePath}/attribute-changes?${this.toQueryParams(query)}`)
            .then(value => ({
                total: value.data.total,
                data: value.data.data.map((item: any) => SourceUpdatesAttributeChangeRow.fromPOJO(item)),
                stats: value.data.stats
            }));
    }

    private toQueryParams(query: SourceUpdatesQuery): string {
        const params = new URLSearchParams();

        if (query.dateFrom) {
            params.set("dateFrom", query.dateFrom);
        }

        if (query.dateTo) {
            params.set("dateTo", query.dateTo);
        }

        if (query.organizationId) {
            params.set("organizationId", query.organizationId);
        }

        if (query.sourceId) {
            params.set("sourceId", query.sourceId);
        }

        this.appendListParam(params, "attributeNames", query.attributeNames);
        this.appendListParam(params, "sourceIds", query.sourceIds);
        this.appendListParam(params, "changeTypes", query.changeTypes);

        if (query.similarityMin !== undefined && query.similarityMin !== null) {
            params.set("similarityMin", query.similarityMin.toString());
        }

        if (query.similarityMax !== undefined && query.similarityMax !== null) {
            params.set("similarityMax", query.similarityMax.toString());
        }

        if (query.numericDifferenceMin !== undefined && query.numericDifferenceMin !== null) {
            params.set("numericDifferenceMin", query.numericDifferenceMin.toString());
        }

        if (query.numericDifferenceMax !== undefined && query.numericDifferenceMax !== null) {
            params.set("numericDifferenceMax", query.numericDifferenceMax.toString());
        }

        if (query.orderBy) {
            params.set("$orderby", query.orderBy);
        }

        if (query.top !== undefined) {
            params.set("$top", query.top.toString());
        }

        if (query.skip !== undefined) {
            params.set("$skip", query.skip.toString());
        }

        return params.toString();
    }

    private appendListParam(params: URLSearchParams, name: string, values?: string[]) {
        values?.forEach(value => params.append(name, value));
    }
}

export default SourceUpdatesService;
